import React, { useState } from 'react';
import { Button } from './ui/button';
import { Send, Loader2 } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

// Interpret the /invoices/bulk-action response. mark_sent returns HTTP 200 with
// { success, failed, errors[] } even when a per-invoice send fails, so success is
// only real when at least one succeeded and none failed.
export function interpretSendResult(data) {
  const d = data || {};
  if ((d.failed || 0) > 0 || (d.success || 0) === 0) {
    const reason = (d.errors && d.errors[0]) || 'Invoice could not be sent';
    return { ok: false, message: `Failed to send invoice: ${reason}` };
  }
  return { ok: true };
}

export default function InvoiceSendButton({ invoice, onSent, className = '' }) {
  const [sending, setSending] = useState(false);
  const ineligible = !invoice || ['paid', 'cancelled'].includes(invoice.status);

  const handleSend = async () => {
    if (!invoice || sending) return;                        // duplicate-click guard
    if (['paid', 'cancelled'].includes(invoice.status)) {   // status eligibility
      toast.error(`Cannot send a ${invoice.status} invoice`);
      return;
    }
    const recipient = invoice.customer_name || 'the customer';
    if (!window.confirm(`Send invoice ${invoice.invoice_number} to ${recipient}?\n\nAn email will be sent and the invoice marked as Sent.`)) return;
    setSending(true);
    try {
      const res = await api.post('/invoices/bulk-action', { invoice_ids: [invoice.id], action: 'mark_sent' });
      const r = interpretSendResult(res.data);
      if (!r.ok) { toast.error(r.message); setSending(false); return; }  // keep dialog open/readable
      toast.success(`Invoice ${invoice.invoice_number} sent to ${recipient}`);
      onSent && onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invoice');
    }
    setSending(false);
  };

  return (
    <Button size="sm" className={`h-7 sm:h-8 text-xs sm:text-sm bg-[#1a5490] hover:bg-[#0f3a5f] ${className}`}
      disabled={sending || ineligible} onClick={handleSend} data-testid="modal-send-invoice-btn">
      {sending ? <Loader2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 animate-spin" /> : <Send className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />}
      <span>{sending ? 'Sending…' : 'Send Invoice'}</span>
    </Button>
  );
}
